/**
 * exercise-view.js
 */

import React from 'react';
import {
  useMediaQuery,
  Typography,
  IconButton,
} from '@mui/material';
import {
  useHistory,
  useParams,
} from "react-router-dom";
import { useTranslation } from 'react-i18next';
import { TOUR_BEACON_VIEW_LIMIT, Types } from 'utils/constants';
import { makeLocation } from 'utils/routing';
import { UserContext } from 'contexts/user/user-context';
import { DogsContext } from 'contexts/dogs/dogs-context';
import { TourContext } from 'contexts/tour/tour-context';
import useRecordStore from 'contexts/record-store';
import EditButton from 'components/edit-button';
import useTours from 'components/tour/tour-data';
import DrawerContent from 'components/menu-drawer/drawer-content';
import MobilePageTitle from 'components/menu-drawer/mobile-page-title';
import IconSection from 'components/icon-section';
import Icon from 'components/icon';
import ApplicableDogs from 'components/applicable-dogs';
import RecordFooter from 'components/record-footer';
import ExerciseViewBase from './exercise-view-base';
import MobileAnswerList from './mobile-answer-list';


// use wrapper to remount component when exercise ID changes
export default function ExerciseView(props) {
  const params = useParams();
  const exerciseId = params.exerciseId || 0;

  return <ExerciseViewImplementation
    exerciseId={exerciseId}
    key={exerciseId}
    {...props}
  />;
}


function ExerciseViewImplementation(props) {
  const {
    exerciseId,
    setDesktopActions,
    setMobileAppBar,
    drawerState,
    onClose,
    basePath,
  } = props;
  const { t } = useTranslation('general');
  const { user } = React.useContext(UserContext);
  const { dogs } = React.useContext(DogsContext);
  const { activeTour, toursSeen, beaconViews } = React.useContext(TourContext);
  let history = useHistory();
  const recordStore = useRecordStore();
  const path = history.location.pathname;
  const exerciseRecord = recordStore.useRecord(Types.Record.EXERCISE, exerciseId).ornull;
  const hasRecord = Boolean(exerciseRecord);
  const isMobileDrawer = useMediaQuery(theme => theme.breakpoints.down('sm_md'), { noSsr: true });
  const { startTour } = useTours();


  // exercise
  const canEdit = exerciseRecord ? exerciseRecord.get().canEdit : false;
  const numApplicableDogs = exerciseRecord && exerciseRecord.get().applicableHandlerDogs
    ? exerciseRecord.get().applicableHandlerDogs.reduce((total, handler) => {
      return handler.dogs.reduce((total, dog) => total + (dog.appliesToExercise ? 1 : 0), total);
    }, 0)
    : null;
  const numTotalDogs = exerciseRecord && exerciseRecord.get().applicableHandlerDogs
    ? exerciseRecord.get().applicableHandlerDogs.reduce((total, handler) => {
      return handler.dogs.reduce((total, dog) => total + 1, total);
    }, 0)
    : null;


  React.useEffect(() => {
    setTimeout(() => drawerState.loading.content.set(!hasRecord));

  // drawerState is hookstate setter
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasRecord]);


  // check if we should start a product tour
  React.useEffect(() => {
    const isTourConditionsValid = activeTour < 0 && !isMobileDrawer && user !== null && dogs.length > 0 && user.completeExercisesCount >= 0;
    const isTourPathValid = path.includes("/view/exercise");
    const beaconViewCount = beaconViews[5] ?? 0;
    if (isTourConditionsValid && isTourPathValid && !toursSeen.includes(5) && beaconViewCount <= TOUR_BEACON_VIEW_LIMIT) {
      setTimeout(() => startTour(5), 1000); // Exercise edit button tour
    }
  }, [path, isMobileDrawer, user, dogs, activeTour, toursSeen, beaconViews, startTour]);


  const onBack = React.useCallback(async () => {
    history.push(makeLocation(
      basePath,
      'training',
      'menu',
      {type: 'exercise', id: exerciseId},
      history.location
    ));
  }, [history, basePath, exerciseId]);


  const onEdit = React.useCallback(() => {
    history.push(makeLocation(
      basePath,
      'training',
      'editExercise',
      {exerciseId},
      history.location
    ));
  }, [history, basePath, exerciseId]);


  const editButton = React.useMemo(() => !user.role.isSupervisor ? (
    <EditButton 
      tooltip={canEdit ? '' : t('NoEditPermission')}
      iconOnly={isMobileDrawer}
      data-testid="edit-button"
      onClick={onEdit}
      disabled={!user.role.status.isActive || !canEdit}
    />
  ) : null, [user, onEdit, canEdit, t, isMobileDrawer]);


  const backButton = React.useMemo(() => (
    <IconButton
      data-testid="back-button"
      size="small"
      style={{color: 'inherit'}}
      onClick={onBack}
    >
      <Icon type='nav-back' />
    </IconButton>
  ), [onBack]);


  const closeButton = React.useMemo(() => (
    <IconButton
      data-testid="mobile-drawer-close-button"
      size="small"
      style={{color: 'inherit'}}
      onClick={onClose}
    >
      <Icon type='mobile-close-drawer' />
    </IconButton>
  ), [onClose]);


  // according to react docs, this should probably be removed.
  // we would accomplish by moving the buttons & actions into their own components.
  // these would then be displayed by the container/view/edit components directly.
  // the container would have to determine the context (view/edit) but that's probably not too complex.
  React.useEffect(() => {
    if (setDesktopActions && !user.role.isSupervisor) {
      setDesktopActions(editButton);
    }
    if (setMobileAppBar) {
      setMobileAppBar({
        title: 'Exercise',
        leftActions: backButton,
        rightActions: <>{closeButton}{editButton}</>,
        recordGroupType: Types.RecordGroup.TRAINING,
      })
    }
  }, [setDesktopActions, setMobileAppBar, user, editButton, closeButton, backButton]);


  return (
    <>
      {exerciseRecord &&
      <>
        <MobilePageTitle title={exerciseRecord.get().name} />

        {/* mobile page answer rows */}
        {isMobileDrawer && <MobileAnswerList {...props} />}

        <DrawerContent>
          <ExerciseViewBase exerciseRecord={exerciseRecord.get()} showName={false} />

          {/* dog applicability */}
          {exerciseRecord.get().applicableHandlerDogs &&
          <IconSection iconName='applicable-dogs'>
            <Typography variant="subtitle2" style={{ marginBottom: 8 }}>
              Applies to {numApplicableDogs} / {numTotalDogs} dogs at this event:
            </Typography>
            <ApplicableDogs handlerList={exerciseRecord.get().applicableHandlerDogs} />
          </IconSection>}

          <RecordFooter
            createdBy={exerciseRecord.get().createdBy}
            dateCreated={exerciseRecord.get().dateCreated}
          />
        </DrawerContent>
      </>}
    </>
  );
}